import { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import {
  Mail, Phone, MapPin, Clock, ArrowRight,
  Linkedin, Twitter, Instagram, Send,
  Sparkles, Star, CheckCircle, Lock
} from 'lucide-react';
import { motion } from "framer-motion";

export default function Footer() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      setTimeout(() => {
        setIsSubscribed(false);
        setEmail('');
      }, 3000);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const navigationLinks = [
    { label: 'Accueil', path: '/' },
    { label: 'Services', path: '/Services' },
    { label: 'À Propos', path: '/Propos' },
    { label: 'Témoignages', path: '/Temoignages' },
    { label: 'Contact', path: '/Contact' },
  ];

  const footerLinks = [
    {
      title: "Solutions",
      links: [
        { label: "Développement Web", path: "/Services" },
        { label: "Applications Mobiles", path: "/Services" },
        { label: "Cybersécurité", path: "/Services" },
        { label: "Audits IT", path: "/Services" },
        { label: "Cloud Computing", path: "/Services" },
      ]
    },
    {
      title: "Entreprise",
      links: [
        { label: "À propos", path: "/About" },
        { label: "Nos réalisations", path: "/Temoignages" },
        { label: "Carrières", path: "/Careers" },
        { label: "Blog", path: "/Blog" },
        { label: "Presse", path: "/Press" },
      ]
    }
  ];

  const contactInfo = [
    { icon: <Mail className="w-5 h-5" />, label: "Email", value: "info@myriade-groupe.com" },
    { icon: <Phone className="w-5 h-5" />, label: "Téléphone", value: "+228 90 04 88 77" },
    { icon: <MapPin className="w-5 h-5" />, label: "Adresse", value: "Lomé, Togo" },
    { icon: <Clock className="w-5 h-5" />, label: "Horaires", value: "Lun-Ven: 8h-17h" },
  ];

  return (
    <footer className="relative overflow-hidden">
      {/* ================================================ */}
      {/* FOND IDENTIQUE AU HERO */}
      {/* ================================================ */}

      {/* Fond de dégradé principal */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-blue-900 z-0" />

      {/* Radial gradient pour effet de profondeur */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(16, 211, 81, 0.1),transparent_50%)] z-0" />

      {/* Étoiles animées identiques au Hero */}
      <div className="absolute inset-0 overflow-hidden z-0">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400/30 rounded-full"
            initial={{
              x: Math.random() * 100 + 'vw',
              y: Math.random() * 100 + 'vh'
            }}
            animate={{
              x: [null, Math.random() * 100 + 'vw'],
              y: [null, Math.random() * 100 + 'vh']
            }}
            transition={{
              duration: 20 + Math.random() * 10,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        ))}
      </div>

      {/* ================================================ */}
      {/* CONTENU DU FOOTER */}
      {/* ================================================ */}
      <div className="relative z-10 border-t border-gray-800/50">
        <div className="container mx-auto px-4 py-16">
          {/* Section principale */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16"
          >
            {/* Logo et description */}
            <motion.div variants={itemVariants} className="lg:col-span-2 flex flex-col items-center md:items-start">
              <div className="flex items-center gap-3 mb-6">

              </div>
              <div className="text-center md:text-left">
                <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 bg-clip-text mb-5 text-transparent">
                  Myriade
                </div>
                <div className="text-xs md:text-sm text-blue-400 font-medium tracking-wider">PROFESSIONAL SOLUTIONS</div>
              </div>


              <p className="text-gray-400 mb-8 max-w-md leading-relaxed text-center md:text-left mx-auto md:mx-0">
                Leader en solutions digitales innovantes, nous transformons vos idées en expériences
                numériques exceptionnelles avec une expertise pointue en développement et cybersécurité.
              </p>

              {/* Newsletter */}
              <div className="mb-8 w-full max-w-md">
                <h3 className="text-white font-semibold mb-4 text-center md:text-left">Restez informé</h3>
                <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Votre email"
                    className="flex-1 px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-colors w-full"
                    required
                  />
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="cursor-pointer px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-900 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300 flex items-center justify-center gap-2 w-full sm:w-auto overflow-hidden whitespace-nowrap"
                  >
                    <span>{isSubscribed ? 'Merci !' : 'S\'inscrire'}</span>
                    <Send className="w-4 h-4 shrink-0" />
                  </motion.button>
                </form>
                {isSubscribed && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-green-400 text-sm mt-2"
                  >
                    Merci pour votre inscription à notre newsletter !
                  </motion.p>
                )}
              </div>
            </motion.div>

            {/* Liens rapides */}
            <motion.div variants={itemVariants} className="text-center md:text-left">
              <h3 className="text-white font-bold text-lg mb-6 relative inline-block">
                Navigation
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 md:left-0 md:translate-x-0 w-12 h-0.5 bg-gradient-to-r from-blue-500 to-blue-900 rounded-full" />
              </h3>
              <ul className="space-y-4">
                {navigationLinks.map((link, idx) => (
                  <li key={idx}>
                    <button
                      onClick={() => navigate(link.path)}
                      className="text-gray-400 hover:text-blue-400 transition-colors duration-300 group flex items-center justify-center md:justify-start gap-2 w-full"
                    >
                      <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity hidden md:block" />
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Solutions */}
            <motion.div variants={itemVariants} className="text-center md:text-left">
              <h3 className="text-white font-bold text-lg mb-6 relative inline-block">
                Solutions
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 md:left-0 md:translate-x-0 w-12 h-0.5 bg-gradient-to-r from-blue-500 to-blue-900 rounded-full" />
              </h3>
              <ul className="space-y-4">
                {footerLinks[0].links.map((link, idx) => (
                  <li key={idx}>
                    <button
                      onClick={() => navigate(link.path)}
                      className="text-gray-400 hover:text-blue-400 transition-colors duration-300 group flex items-center justify-center md:justify-start gap-2 w-full"
                    >
                      <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity hidden md:block" />
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Contact */}
            <motion.div variants={itemVariants} className="text-center md:text-left">
              <h3 className="text-white font-bold text-lg mb-6 relative inline-block">
                Contact
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 md:left-0 md:translate-x-0 w-12 h-0.5 bg-gradient-to-r from-blue-500 to-blue-900 rounded-full" />
              </h3>
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex flex-col items-center md:items-start md:flex-row group">
                    <div className="w-5 h-5 mb-2 md:mb-0 md:mt-1 md:mr-3 text-blue-400 group-hover:text-blue-300 transition-colors">
                      {info.icon}
                    </div>
                    <div className="flex flex-col items-center md:items-start">
                      <p className="font-medium text-xs md:text-sm text-gray-300">{info.label}</p>
                      <p className="text-gray-400 text-sm group-hover:text-white transition-colors">
                        {info.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Diviseur       <div className="border-t border-gray-800/50 mb-8"></div> */}

          {/* Section inférieure */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            {/* Copyright */}
            <div className="text-gray-500 text-sm">
              Myriade SARL © {new Date().getFullYear()} Tous droits réservés.
            </div>

            {/* Mentions légales */}
            <div className="flex items-center gap-6 text-sm">
              <button
                // onClick={() => navigate("/Privacy")}
                className="text-gray-500 hover:text-blue-400 transition-colors"
              >
                Confidentialité
              </button>
              <button
                // onClick={() => navigate("/Terms")}
                className="text-gray-500 hover:text-blue-400 transition-colors"
              >
                Conditions
              </button>


            </div>

          </div>

          {/* Badge de sécurité */}

        </div>

        {/* Effet de glow au bas du footer */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />
      </div>
    </footer>
  );
}
